// ⌘K command palette for the Settings hub. Fuzzy-searches every visible
// settings section and navigates to one by keyboard or mouse. Rendered (and
// unmounted) by SettingsHubLayout — mounting is the open state, so the query
// and selected index reset naturally each time it opens.
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search } from 'lucide-react';
import clsx from 'clsx';
import { SettingsSection } from './settingsSections';

interface SettingsCommandPaletteProps {
  // Sections the current user may navigate to (already entitlement-filtered).
  sections: SettingsSection[];
  // Section ids, most-recent-first, for the "Recent" group.
  recents: string[];
  onSelect: (section: SettingsSection) => void;
  onClose: () => void;
}

// Case-insensitive subsequence match: every character of `query` appears in
// `label`, in order, not necessarily contiguous. e.g. "tem" matches both
// "Templates" and "Custom Fields".
function fuzzyMatch(query: string, label: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  let i = 0;
  for (const ch of label.toLowerCase()) {
    if (ch === q[i]) i += 1;
    if (i === q.length) return true;
  }
  return false;
}

// Wrap the first contiguous occurrence of `query` inside `label` in an
// accent-colored span.
function highlightMatch(label: string, query: string): React.ReactNode {
  if (!query) return label;
  const idx = label.toLowerCase().indexOf(query.toLowerCase());
  if (idx < 0) return label;
  return (
    <>
      {label.slice(0, idx)}
      <span className="text-accent">{label.slice(idx, idx + query.length)}</span>
      {label.slice(idx + query.length)}
    </>
  );
}

const EYEBROW =
  'px-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500';

const SettingsCommandPalette: React.FC<SettingsCommandPaletteProps> = ({
  sections,
  recents,
  onSelect,
  onClose,
}) => {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  const matches = useMemo(
    () => sections.filter((s) => fuzzyMatch(query, s.label)),
    [sections, query],
  );

  const recentSections = useMemo(() => {
    if (query) return [];
    return recents
      .map((id) => sections.find((s) => s.id === id))
      .filter((s): s is SettingsSection => Boolean(s));
  }, [recents, sections, query]);

  // Autofocus the input shortly after mount so the opening transition isn't
  // interrupted by the focus jump.
  useEffect(() => {
    const timer = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(timer);
  }, []);

  // Keep the highlighted index in range as the result set shrinks.
  useEffect(() => {
    setActiveIdx((i) => Math.min(i, Math.max(0, matches.length - 1)));
  }, [matches.length]);

  const optionId = (i: number) => `${listboxId}-option-${i}`;

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(matches.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = matches[activeIdx];
      if (selected) onSelect(selected);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return createPortal(
    <div
      data-testid="settings-palette-backdrop"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center px-5 pt-24 bg-surface-0/70 backdrop-blur-sm animate-fade-in"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search settings"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          // Trap focus inside the palette — arrow keys drive selection.
          if (e.key === 'Tab') e.preventDefault();
        }}
        className="w-[640px] max-w-full bg-surface-1 border border-white/[0.16] rounded-ds-2xl shadow-ds-e3 overflow-hidden"
      >
        <div className="flex items-center gap-2.5 px-4 py-3.5 border-b border-white/[0.08]">
          <Search className="w-4 h-4 shrink-0 text-slate-500" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-label="Search settings"
            aria-activedescendant={
              matches[activeIdx] ? optionId(activeIdx) : undefined
            }
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIdx(0);
            }}
            onKeyDown={handleInputKeyDown}
            placeholder="Type a setting name…"
            className="flex-1 bg-transparent border-0 outline-none text-base text-slate-50 placeholder:text-slate-500"
          />
          <kbd className="font-mono text-[11px] text-slate-500 border border-white/[0.08] rounded px-1.5 py-0.5">
            esc
          </kbd>
        </div>

        <div className="max-h-[360px] overflow-y-auto px-2 py-2.5">
          {recentSections.length > 0 && (
            <>
              <div className={clsx(EYEBROW, 'pt-1.5 pb-1')}>Recent</div>
              {recentSections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={`recent-${section.id}`}
                    type="button"
                    onClick={() => onSelect(section)}
                    className="flex w-full items-center gap-3 px-3 py-2 rounded-ds-md text-left transition-colors hover:bg-surface-2"
                  >
                    <Icon className="w-4 h-4 shrink-0 text-slate-500" aria-hidden="true" />
                    <span className="text-[13px] text-slate-50">{section.label}</span>
                    <span className="ml-auto text-xs text-slate-500">{section.group}</span>
                  </button>
                );
              })}
            </>
          )}

          <div className={clsx(EYEBROW, 'pt-3 pb-1')}>
            {query ? `Results · ${matches.length}` : 'All settings'}
          </div>

          <ul role="listbox" id={listboxId} aria-label="Settings sections">
            {matches.map((section, i) => {
              const Icon = section.icon;
              const isActive = i === activeIdx;
              return (
                <li
                  key={section.id}
                  id={optionId(i)}
                  role="option"
                  aria-selected={isActive}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => onSelect(section)}
                  className={clsx(
                    'flex items-center gap-3 px-3 py-2.5 rounded-ds-md text-[13px] cursor-pointer',
                    isActive ? 'bg-accent-tint text-slate-50' : 'text-slate-400',
                  )}
                >
                  <Icon
                    className={clsx(
                      'w-4 h-4 shrink-0',
                      isActive ? 'text-accent' : 'text-slate-500',
                    )}
                    aria-hidden="true"
                  />
                  <span className={isActive ? 'text-slate-50' : 'text-slate-400'}>
                    {highlightMatch(section.label, query)}
                  </span>
                  <span className="ml-auto text-[11px] text-slate-500">
                    {section.group}
                  </span>
                  {isActive && (
                    <kbd className="font-mono text-[11px] text-slate-500 border border-white/[0.08] rounded px-1.5 py-0.5">
                      ↵
                    </kbd>
                  )}
                </li>
              );
            })}
          </ul>

          {matches.length === 0 && (
            <div className="px-3 py-6 text-center text-[13px] text-slate-500">
              No settings match “{query}”.
            </div>
          )}
        </div>

        <div className="flex items-center gap-3.5 px-3.5 py-2 border-t border-white/[0.08] text-[11px] text-slate-500">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>esc close</span>
          <span className="ml-auto">{sections.length} sections</span>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SettingsCommandPalette;
